import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Switch, Typography } from '@mui/material';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SelectDropdown from '../../components/SelectDropdown';
import Table from '../../components/Table';
import { ShimmerComponent } from '../../components/Shimmer/ShimmerComponent';
import BouncingLogo from '../../components/Shimmer/BouncingLogo';
import { useSelector, useDispatch } from 'react-redux';
import selectors from '../../modules/selectors/datePickerSelectors';
import { useMutation } from '@tanstack/react-query';
import api from '../../services/api';
import DatePicker from '../../components/DatePicker';
import {
  AUDIENCE_ANALYSIS,
  COMPARISON_PERIOD,
  TIME_PERIOD,
  WEEK_SUBSTRACTION_VALUE,
} from '../../constants/datePickerConstants';
import { TransformDate } from '../../components/DatePicker/DatePickerUtils/datePickerUtils';
import { audienceAnalysisActions } from '../../modules/reducer/audienceAnalysisSlice';
import DownloadButton from '../../components/DownloadReport';
import { previousWeekExtractor } from '../../data/branchPerformanceData';
import { currentYearAndQuarterFun } from '../../constants/utils';
import {
  comparisonFormatter,
  Tiers,
  tiersData,
  configureTableData,
  configureDownloadTableData,
  HOUSEHOLD_COMPONENT,
  AUDIENCE_ANALYSIS_COMPONENT,
} from './audienceAnalysisService';
import { COMPONENT_QUERY } from '../../constants/api';

// Extracted Cell Components with PropTypes
const ExpandCell = ({ row }) => {
  return row.values.expand ? (
    <span {...row.getToggleRowExpandedProps()}>
      {row.isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
    </span>
  ) : (
    <span></span>
  );
};

ExpandCell.propTypes = {
  row: PropTypes.shape({
    values: PropTypes.shape({
      expand: PropTypes.bool,
    }),
    isExpanded: PropTypes.bool,
    getToggleRowExpandedProps: PropTypes.func,
  }).isRequired,
};

const ChannelsCell = ({ cell }) => {
  return (
    <span style={cell.row.original?.isSubrow ? { fontWeight: 'bold' } : {}}>{cell.value}</span>
  );
};

ChannelsCell.propTypes = {
  cell: PropTypes.shape({
    value: PropTypes.string,
    row: PropTypes.shape({
      original: PropTypes.shape({
        isSubrow: PropTypes.bool,
      }),
    }),
  }).isRequired,
};

const TotalCell = ({ row, toggleValue }) => {
  const totalData = row?.original?.total;
  const hasData = totalData?.total !== undefined || totalData?.total === 0;

  return (
    <div>
      {hasData ? comparisonFormatter(totalData.total, totalData.keyValue, toggleValue) : 'N/A'}
    </div>
  );
};

TotalCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      total: PropTypes.shape({
        total: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        keyValue: PropTypes.object,
      }),
    }),
  }).isRequired,
  toggleValue: PropTypes.bool.isRequired,
};

const NonAddressableCell = ({ row, toggleValue }) => {
  const nonAddressableData = row?.original?.nonAddressable;
  const hasData =
    nonAddressableData?.nonAddressable !== undefined || nonAddressableData?.nonAddressable === 0;

  return (
    <div>
      {hasData
        ? comparisonFormatter(
            nonAddressableData.nonAddressable,
            nonAddressableData.keyValue,
            toggleValue
          )
        : 'N/A'}
    </div>
  );
};

NonAddressableCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      nonAddressable: PropTypes.shape({
        nonAddressable: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        keyValue: PropTypes.object,
      }),
    }),
  }).isRequired,
  toggleValue: PropTypes.bool.isRequired,
};

const NurtureCell = ({ row, toggleValue }) => {
  const nurtureData = row?.original?.nurture;
  const hasData = nurtureData?.nurture !== undefined || nurtureData?.nurture === 0;

  return (
    <div>
      {hasData
        ? comparisonFormatter(nurtureData.nurture, nurtureData.keyValue, toggleValue)
        : 'N/A'}
    </div>
  );
};

NurtureCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      nurture: PropTypes.shape({
        nurture: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        keyValue: PropTypes.object,
      }),
    }),
  }).isRequired,
  toggleValue: PropTypes.bool.isRequired,
};

// Wrapper functions to avoid inline component definitions
const createTotalCellRenderer = (toggleValue) => (props) => (
  <TotalCell {...props} toggleValue={toggleValue} />
);

const createNonAddressableCellRenderer = (toggleValue) => (props) => (
  <NonAddressableCell {...props} toggleValue={toggleValue} />
);

const createNurtureCellRenderer = (toggleValue) => (props) => (
  <NurtureCell {...props} toggleValue={toggleValue} />
);

const AudienceAnalysis = () => {
  const { userStores, userData } = useSelector((state) => state.auth);
  const options = useMemo(() => {
    const stores = userStores.map((store) => ({
      label: store.branch_name,
      value: store.branch_number,
      ...store,
    }));
    const GaleMarkets = stores.filter((store) => store.value === 1);
    if (GaleMarkets?.length) {
      return [
        GaleMarkets[0],
        ...stores
          .filter((store) => store.value !== 1)
          .sort((a, b) => a.label.localeCompare(b.label)),
      ];
    } else {
      return [
        ...stores
          .filter((store) => store.value !== 1)
          .sort((a, b) => a.label.localeCompare(b.label)),
      ];
    }
  }, [userStores]);
  const tiersId = userData?.data?.data_sources?.find(
    (data) => data?.name === 'audience_analysis_include_tier_d'
  )?.identifier;
  const householdId = userData?.data?.data_sources?.find(
    (data) => data?.name === 'audience_analysis_households'
  )?.identifier;
  const [storeId, setStoreId] = useState(options[0].value);
  const [tierColumns, setTierColumns] = useState(Tiers.map((tier) => tier.value));
  const [tableDataArray, setTableDataArray] = useState([]);
  const { audienceAnalysisRange, compareAudienceAnalysisRange } = useSelector(
    (state) => state.audienceAnalysis
  );
  const { transformedData, transformedDataWithDate } = useSelector(
    selectors.datePickerCombinedData
  );
  const [defaultFilter, setDefaultFilter] = useState(TIME_PERIOD);
  const [compareFilter, setCompareFilter] = useState(COMPARISON_PERIOD);
  const [currentFilterVal, setCurrentFilterVal] = useState([]);
  const [compareFilterVal, setCompareFilterVal] = useState([]);
  const [currentHouseholdFilter, setCurrentHouseholdFilter] = useState([]);
  const [compareHouseholdFilter, setCompareHouseholdFilter] = useState([]);
  const dispatch = useDispatch();
  const storeArray = userData?.data?.stores_data?.map((store) => store.branch_number);
  const storeList = storeArray;
  const [isDownload, setIsDownload] = useState(false);
  const [downloadReportData, setDownloadReportData] = useState([]);
  const [toggleValue, setToggleValue] = useState(false);
  const tiersArray = tiersData(toggleValue);
  const checkForGlobalObject = (object) => {
    if (object === null || object === undefined) {
      return false; // Return false if object is null or undefined
    }
    return (
      Object.values(object).every((value) => value === null || value === undefined) ||
      Object.values(object).some((value) => value === null || value === undefined)
    );
  };

  useEffect(() => {
    timePeriodRangeHandler();
  }, [transformedDataWithDate]);

  useEffect(() => {
    comparisonPeriodRangeHandler();
  }, [audienceAnalysisRange]);

  const formatWeekValue = (week) => {
    return week <= 9 && week > 0 ? `0${week}` : week;
  };

  const createDateIndexValue = (year, week) => {
    return year?.toString()?.concat(formatWeekValue(week));
  };

  const createHouseholdRangeFilters = (startYear, endYear) => [
    { col: 'fiscal_year', op: '>=', val: startYear },
    { col: 'fiscal_year', op: '<=', val: endYear },
  ];

  const processCurrentFilters = (range, storeIdFilter) => {
    const householdRange = createHouseholdRangeFilters(range?.start_year, range?.end_year);

    setCurrentFilterVal([
      {
        col: 'dateindex',
        op: '>=',
        val: createDateIndexValue(range?.start_year, range?.start_week),
      },
      {
        col: 'dateindex',
        op: '<=',
        val: createDateIndexValue(range?.end_year, range?.end_week),
      },
      storeIdFilter,
    ]);

    setCurrentHouseholdFilter([
      ...(range?.start_year === range?.end_year
        ? [{ col: 'fiscal_year', op: '==', val: range?.start_year }]
        : householdRange),
      storeIdFilter,
    ]);
  };

  const processCompareFilters = (range, storeIdFilter) => {
    const householdRange = createHouseholdRangeFilters(range?.start_year, range?.end_year);

    setCompareFilterVal([
      {
        col: 'dateindex',
        op: '>=',
        val: createDateIndexValue(range?.start_year, range?.start_week),
      },
      {
        col: 'dateindex',
        op: '<=',
        val: createDateIndexValue(range?.end_year, range?.end_week),
      },
      storeIdFilter,
    ]);

    setCompareHouseholdFilter([
      ...(range?.start_year === range?.end_year
        ? [{ col: 'fiscal_year', op: '==', val: range?.start_year }]
        : householdRange),
      storeIdFilter,
    ]);
  };

  useEffect(() => {
    const storeIdFilter = {
      col: 'store_id',
      op: 'in',
      val: isDownload ? storeArray : [storeId],
    };

    if (!checkForGlobalObject(audienceAnalysisRange)) {
      processCurrentFilters(audienceAnalysisRange, storeIdFilter);
    }

    if (!checkForGlobalObject(compareAudienceAnalysisRange)) {
      processCompareFilters(compareAudienceAnalysisRange, storeIdFilter);
    }
  }, [audienceAnalysisRange, compareAudienceAnalysisRange, storeId, isDownload]);

  const timePeriodRangeHandler = () => {
    const allValuesAreNull = checkForGlobalObject(audienceAnalysisRange);
    const obj = TransformDate?.getCurrentYearAndQuarter(transformedDataWithDate);
    if (allValuesAreNull && obj && Object.keys(obj).length && transformedDataWithDate?.length > 0) {
      const quarterValue =
        transformedDataWithDate?.length &&
        TransformDate?.quickFilters?.TWELVE_WEEK_DATA(
          transformedDataWithDate,
          previousWeekExtractor,
          WEEK_SUBSTRACTION_VALUE
        );
      dispatch(audienceAnalysisActions.setAudienceAnalysisRange(quarterValue));
      const value = `w${audienceAnalysisRange?.start_week}, ${audienceAnalysisRange?.start_year} - w${audienceAnalysisRange?.end_week}, ${audienceAnalysisRange?.end_year}`;
      setDefaultFilter(value);
    }
  };

  const comparisonPeriodRangeHandler = () => {
    const allValuesAreNullForCurr = checkForGlobalObject(audienceAnalysisRange);
    if (!allValuesAreNullForCurr) {
      const range = TransformDate.comparisonPeriod.previousPeriod(audienceAnalysisRange);
      const value = `w${range?.start_week}, ${range?.start_year} - w${range?.end_week}, ${range?.end_year}`;
      setCompareFilter(value);
      dispatch(audienceAnalysisActions.setCompareAudienceAnalysisRange(range));
    }
  };

  const quarterAndYearValueHandler = () => {
    if (transformedDataWithDate?.length > 0) {
      const currentYearAndQuarter = currentYearAndQuarterFun(
        transformedDataWithDate,
        previousWeekExtractor,
        WEEK_SUBSTRACTION_VALUE,
        TransformDate
      );
      dispatch(
        audienceAnalysisActions.setCurrentQuarter(currentYearAndQuarter?.currentQuarterValue)
      );
      dispatch(
        audienceAnalysisActions.setCurrentYear(currentYearAndQuarter?.currentYearBasedOnWeek)
      );
    }
  };

  const comparisonQuarterAndYearValueHandler = () => {
    const allValuesAreNullForCurr = checkForGlobalObject(audienceAnalysisRange);
    const obj = TransformDate?.getCurrentYearAndQuarter(transformedDataWithDate);
    if (!allValuesAreNullForCurr && obj && Object.keys(obj).length) {
      const year = obj.year - 1;
      const week = audienceAnalysisRange?.start_week;
      const previousValue =
        transformedData.length && TransformDate.findQuarter(year, week, transformedData);
      dispatch(audienceAnalysisActions.setCompareSelectedQuarter(previousValue));
      if (obj.quarter === 'Q1') {
        dispatch(audienceAnalysisActions.setCompareSelectedYear(year - 1));
      } else {
        dispatch(audienceAnalysisActions.setCompareSelectedYear(year));
      }
    }
  };

  const getPayloadAudience = (key, appliedFilters) => {
    const payload = {
      [key]: {
        filters: appliedFilters,
      },
    };
    return payload;
  };

  useEffect(() => {
    quarterAndYearValueHandler();
  }, [transformedDataWithDate]);

  useEffect(() => {
    comparisonQuarterAndYearValueHandler();
  }, [audienceAnalysisRange, transformedDataWithDate]);

  const fetchAudienceAnalysisDataCurrent = async (data) => {
    try {
      const component = AUDIENCE_ANALYSIS_COMPONENT;
      const result = await api.post(
        COMPONENT_QUERY + `?component=${component}`,
        getPayloadAudience(component, data?.currentFilterVal)
      );
      return result?.data;
    } catch (error) {
      console.error(error);
    }
  };

  const fetchAudienceAnalysisDataCompare = async (data) => {
    try {
      const component = AUDIENCE_ANALYSIS_COMPONENT;
      const result = await api.post(
        COMPONENT_QUERY + `?component=${component}`,
        getPayloadAudience(component, data?.compareFilterVal)
      );
      return result.data;
    } catch (error) {
      console.error(error);
    }
  };

  const fetchHouseholdAvailableCurrent = async (data) => {
    try {
      const result = await api.post(
        COMPONENT_QUERY + `?component=${HOUSEHOLD_COMPONENT}`,
        getPayloadAudience(HOUSEHOLD_COMPONENT, data?.currentHouseholdFilter)
      );
      return result?.data;
    } catch (error) {
      console.error(error);
    }
  };

  const fetchHouseholdAvailableCompare = async (data) => {
    try {
      const result = await api.post(
        COMPONENT_QUERY + `?component=${HOUSEHOLD_COMPONENT}`,
        getPayloadAudience(HOUSEHOLD_COMPONENT, data?.compareHouseholdFilter)
      );

      return result.data;
    } catch (error) {
      console.error(error);
    }
  };

  const {
    data: audienceAnalysisDataCurrent,
    mutate: audienceAnalysisCurrentMutation,
    isLoading: isLoadingAudienceAnalysisCurrentData,
    isSuccess: isSuccessAudienceAnalysisCurrentData,
  } = useMutation({
    mutationFn: fetchAudienceAnalysisDataCurrent,
  });

  const {
    data: audienceAnalysisCompareData,
    mutate: audienceAnalysisCompareMutation,
    isLoading: isLoadingAudienceAnalysisCompareData,
    isSuccess: isSuccessAudienceAnalysisComapareData,
  } = useMutation({
    mutationFn: fetchAudienceAnalysisDataCompare,
  });

  const {
    data: householdAvailableCurrentData,
    mutate: householdAvailableCurrentMutation,
    isLoading: isLoadingHouseholdAvailableCurrentData,
    isSuccess: isSuccessHouseholdAvailableCurrentData,
  } = useMutation({
    mutationFn: fetchHouseholdAvailableCurrent,
  });

  const {
    data: householdAvailableCompareData,
    mutate: householdAvailableCompareMutation,
    isLoading: isLoadingHouseholdAvailableCompareData,
    isSuccess: isSuccessHouseholdAvailableCompareData,
  } = useMutation({
    mutationFn: fetchHouseholdAvailableCompare,
  });

  useEffect(() => {
    if (
      storeId &&
      currentFilterVal?.length > 0 &&
      compareFilterVal?.length > 0 &&
      currentHouseholdFilter?.length > 0 &&
      compareHouseholdFilter?.length > 0 &&
      householdId &&
      tiersId
    ) {
      householdAvailableCurrentMutation({ currentHouseholdFilter });
      householdAvailableCompareMutation({ compareHouseholdFilter });
      audienceAnalysisCurrentMutation({ currentFilterVal });
      audienceAnalysisCompareMutation({ compareFilterVal });
    }
  }, [
    storeId,
    tiersId,
    householdId,
    currentFilterVal,
    currentHouseholdFilter,
    compareHouseholdFilter,
    isDownload,
  ]);

  useEffect(() => {
    const key = AUDIENCE_ANALYSIS_COMPONENT;
    const audienceAnalysisCurrent = audienceAnalysisDataCurrent?.data?.[key]?.result;
    const householdAvailableCurrent = householdAvailableCurrentData?.data?.household_data?.result;
    const audienceAnalysisCompare = audienceAnalysisCompareData?.data?.[key]?.result;
    const householdAvailableCompare = householdAvailableCompareData?.data?.household_data?.result;

    if (
      isSuccessAudienceAnalysisCurrentData &&
      isSuccessHouseholdAvailableCurrentData &&
      isSuccessAudienceAnalysisComapareData &&
      isSuccessHouseholdAvailableCompareData &&
      householdAvailableCompare
    ) {
      const result = configureTableData({
        isDownload,
        options,
        storeId,
        audienceAnalysisCurrent,
        householdAvailableCurrent,
        audienceAnalysisCompare,
        householdAvailableCompare,
      });
      setTableDataArray(result?.find((store) => store.store_id === storeId)?.rows);
    }
  }, [
    isSuccessAudienceAnalysisCurrentData,
    isSuccessHouseholdAvailableCurrentData,
    audienceAnalysisDataCurrent,
    householdAvailableCurrentData,
    isSuccessAudienceAnalysisComapareData,
    isSuccessHouseholdAvailableCompareData,
    audienceAnalysisCompareData,
    householdAvailableCompareData,
  ]);

  // useEffect added to resolve download report issue for the years 2019,2020 with separate calulation functions for download report
  useEffect(() => {
    const key = AUDIENCE_ANALYSIS_COMPONENT;
    const audienceAnalysisCurrent = audienceAnalysisDataCurrent?.data?.[key]?.result;
    const householdAvailableCurrent = householdAvailableCurrentData?.data?.household_data?.result;
    const audienceAnalysisCompare = audienceAnalysisCompareData?.data?.[key]?.result;
    const householdAvailableCompare = householdAvailableCompareData?.data?.household_data?.result;

    if (
      isSuccessAudienceAnalysisCurrentData &&
      isSuccessHouseholdAvailableCurrentData &&
      isSuccessAudienceAnalysisComapareData &&
      isSuccessHouseholdAvailableCompareData
    ) {
      const result = configureDownloadTableData({
        isDownload,
        options,
        storeId,
        audienceAnalysisCurrent,
        householdAvailableCurrent,
        audienceAnalysisCompare,
        householdAvailableCompare,
        downloadData: true,
      });
      setDownloadReportData(result);
      setIsDownload(false);
    }
  }, [
    isSuccessAudienceAnalysisCurrentData,
    isSuccessHouseholdAvailableCurrentData,
    audienceAnalysisDataCurrent,
    householdAvailableCurrentData,
    isSuccessAudienceAnalysisComapareData,
    isSuccessHouseholdAvailableCompareData,
    audienceAnalysisCompareData,
    householdAvailableCompareData,
  ]);

  const tableData = useMemo(() => tableDataArray, [tierColumns, tableDataArray]);

  const columns = useMemo(() => {
    return [
      {
        Header: ' ',
        accessor: 'propertyName',
        enableRowSpan: true,
      },
      {
        header: '',
        accessor: 'expand',
        Cell: ExpandCell,
      },
      {
        Header: 'Quarter',
        accessor: 'channels',
        Cell: ChannelsCell,
      },
      {
        Header: 'Total',
        accessor: 'total',
        Cell: createTotalCellRenderer(toggleValue),
      },
      ...tiersArray.filter((tier) => tierColumns.includes(tier.accessor)),
      {
        Header: 'Non Addressable',
        accessor: 'nonAddressable',
        Cell: createNonAddressableCellRenderer(toggleValue),
      },
      {
        Header: 'Nurture',
        accessor: 'nurture',
        Cell: createNurtureCellRenderer(toggleValue),
      },
    ];
  }, [tierColumns, toggleValue]);

  const handleStoreIdChange = (store) => {
    setStoreId(store);
  };

  const handleTiersChange = (tiers) => {
    setTierColumns(tiers);
  };
  const handleToggleChange = () => {
    setToggleValue((prevValue) => !prevValue);
  };

  return (
    <div
      style={{
        boxSizing: 'border-box',
        height: '88vh',
        overflow: 'scroll',
        paddingBottom: '100px',
      }}
    >
      <div style={{ width: 'fitContent', marginBottom: '55px', marginTop: '20px' }}>
        <div
          style={{
            width: '90%',
            textAlign: 'left',
            margin: 'auto',
            fontSize: '1.5rem',
            fontWeight: '500',
            lineHeight: '1.6',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0px 20px',
          }}
        >
          <span>Audience Analysis - All Channels and Tiers</span>
        </div>
      </div>
      <div
        style={{
          padding: '0 20px',
          display: 'flex',
          width: '90%',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: '10px',
          margin: 'auto',
        }}
      >
        <div style={{ marginRight: 'auto', display: 'flex', gap: '15px' }}>
          <SelectDropdown
            options={options}
            label={'Select Branch'}
            singleSelectDefaultValue={options[0].value}
            width={'200px'}
            handleFilterChange={handleStoreIdChange}
            marginExist={false}
            disabled={
              isLoadingHouseholdAvailableCurrentData ||
              isLoadingAudienceAnalysisCurrentData ||
              isLoadingHouseholdAvailableCompareData ||
              isLoadingAudienceAnalysisCompareData
            }
          />
          <SelectDropdown
            options={Tiers}
            multiple={true}
            allOptionsSelectedText={'All Tiers'}
            label={'Select Tiers'}
            multiSelectDefaultValues={[...Tiers.map((tier) => tier.value)]}
            width={'200px'}
            handleFilterChange={handleTiersChange}
            marginExist={false}
            disabled={
              isLoadingHouseholdAvailableCurrentData ||
              isLoadingAudienceAnalysisCurrentData ||
              isLoadingHouseholdAvailableCompareData ||
              isLoadingAudienceAnalysisCompareData
            }
          />
        </div>
        <DatePicker
          setFilter={defaultFilter}
          component={AUDIENCE_ANALYSIS}
          compare={compareFilter}
          combineCompare={true}
        />
        <DownloadButton
          data={downloadReportData}
          downloadCheck={
            storeList?.length === 1
              ? downloadReportData?.length === storeList?.length && isDownload
              : downloadReportData?.length === storeList?.length
          }
          filename="audience_analysis.xlsx"
          setIsDownload={setIsDownload}
          tab={'audience_analysis'}
          isDisabled={
            isLoadingHouseholdAvailableCurrentData ||
            isLoadingAudienceAnalysisCurrentData ||
            isLoadingHouseholdAvailableCompareData ||
            isLoadingAudienceAnalysisCompareData ||
            downloadReportData?.length === 0 ||
            !downloadReportData?.some((store) => store.store_id === storeId)
          }
          isAudienceAnalysisTab
        />
      </div>
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="flex-end"
        alignItems="center"
        width="90%"
        margin="auto"
        padding="15px 10px 0px 0px"
      >
        <Typography
          variant="body1"
          style={{ color: 'rgba(0, 0, 0, 0.6)', lineHeight: '1.9em', fontSize: '0.8rem' }}
        >
          Comparison at Tier Level
        </Typography>
        <Switch
          checked={toggleValue}
          onChange={handleToggleChange}
          color="primary"
          inputProps={{ 'aria-label': 'toggle comparison' }}
          disabled={
            isLoadingHouseholdAvailableCurrentData ||
            isLoadingAudienceAnalysisCurrentData ||
            isLoadingHouseholdAvailableCompareData ||
            isLoadingAudienceAnalysisCompareData
          }
        />
      </Box>
      <div>
        <div style={{ width: '90%', margin: 'auto', padding: '0 20px' }}>
          {isLoadingHouseholdAvailableCurrentData ||
          isLoadingAudienceAnalysisCurrentData ||
          isLoadingHouseholdAvailableCompareData ||
          isLoadingAudienceAnalysisCompareData ||
          downloadReportData?.length === 0 ||
          !downloadReportData?.some((store) => store.store_id === storeId) ? (
            <ShimmerComponent
              updatedStyle={{
                width: '100%',
                height: '100%',
                backgroundSize: '2000px 800px',
                border: '0px',
              }}
            >
              <BouncingLogo height="72vh" text="Loading..." />
            </ShimmerComponent>
          ) : (
            tableData?.length > 0 && (
              <Table data={tableData} columns={columns} audienceAnalysisStyle />
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default AudienceAnalysis;
